(function () {
  "use strict";

  /* ── constants ─────────────────────────────────────────── */
  const SECTIONS   = ["main-cart-items", "header"];   // cart-drawer is a snippet, NOT a section — can't use section rendering API for it
  const DRAWER_ID  = "cart-drawer";
  const BODY_OPEN_CLASS = "drawer-open";

  let drawerTriggerEl = null;
  let isMutating = false;

  /* ── helpers ────────────────────────────────────────────── */

  async function cartFetch(endpoint, body = null) {
    const opts = {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(endpoint, opts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ description: res.statusText }));
      throw new Error(err.description || "Cart error");
    }
    return res.json();
  }

  /* ── re-render sections + cart drawer ──────────────────── */

  async function reRenderSections() {
    // Build section-rendering request for real sections (header, main-cart-items)
    const sectionUrl =
      window.location.pathname + "?sections=" + SECTIONS.join(",");

    // Fetch both in parallel: section rendering API for header/cart-page,
    // and the full page HTML so we can extract the updated cart drawer snippet.
    const [sectionRes, pageRes] = await Promise.all([
      fetch(sectionUrl, { headers: { Accept: "application/json" } }),
      fetch(window.location.pathname, { headers: { Accept: "text/html" } }),
    ]);

    // 1. Update real sections via section rendering API
    if (sectionRes.ok) {
      const data = await sectionRes.json();
      for (const [sectionId, html] of Object.entries(data)) {
        if (!html) continue;
        const wrapper = document.getElementById(
          "shopify-section-" + sectionId
        );
        if (!wrapper) continue;
        const scratch = document.createElement("div");
        scratch.innerHTML = html;
        const inner = scratch.querySelector(
          "#shopify-section-" + sectionId
        );
        wrapper.innerHTML = inner ? inner.innerHTML : html;
      }
    }

    // 2. Update cart drawer by extracting it from the full-page response
    if (pageRes.ok) {
      const pageHtml = await pageRes.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(pageHtml, "text/html");
      const freshDrawer = doc.getElementById(DRAWER_ID);
      const currentDrawer = document.getElementById(DRAWER_ID);
      if (freshDrawer && currentDrawer) {
        // Preserve open/closed state across the swap
        const wasOpen =
          currentDrawer.getAttribute("aria-hidden") === "false";
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        if (wasOpen) {
          currentDrawer.setAttribute("aria-hidden", "false");
          document.body.classList.add(BODY_OPEN_CLASS);
        }
      }
    }

    wireCartPage();
    wireDrawer();
  }

  /* ── toast ──────────────────────────────────────────────── */

  function showToast(msg, isError = false) {
    const existing = document.querySelector(".bgf-toast");
    if (existing) existing.remove();
    const toast = document.createElement("div");
    toast.className = "bgf-toast" + (isError ? " bgf-toast--error" : "");
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    setTimeout(() => {
      toast.classList.remove("is-visible");
      setTimeout(() => toast.remove(), 300);
    }, 2400);
  }

  /* ── mutate wrapper ────────────────────────────────────── */

  async function mutate(fn, successMsg = null) {
    if (isMutating) return;
    isMutating = true;
    try {
      await fn();
      await reRenderSections();
      if (successMsg) showToast(successMsg);
    } catch (err) {
      showToast(err.message || "Something went wrong. Try again.", true);
    } finally {
      isMutating = false;
    }
  }

  /* ── drawer open / close ───────────────────────────────── */

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function openDrawer(triggerEl = null) {
    const drawer = getDrawer();
    if (!drawer) return;
    drawerTriggerEl = triggerEl;
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add(BODY_OPEN_CLASS);
    const closeBtn = drawer.querySelector("[data-drawer-close]");
    if (closeBtn && closeBtn.tagName === "BUTTON") closeBtn.focus();
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove(BODY_OPEN_CLASS);
    if (drawerTriggerEl) {
      drawerTriggerEl.focus();
      drawerTriggerEl = null;
    }
  }

  window.BGFCart = { openDrawer, closeDrawer };

  /* ── wire drawer controls ──────────────────────────────── */

  function wireDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.querySelectorAll("[data-drawer-close]").forEach((el) => {
      el.removeEventListener("click", onDrawerClose);
      el.addEventListener("click", onDrawerClose);
    });
    drawer.querySelectorAll(".qty-btn").forEach((btn) => {
      btn.removeEventListener("click", onQtyClick);
      btn.addEventListener("click", onQtyClick);
    });
    drawer.querySelectorAll('[data-action="remove"]').forEach((btn) => {
      btn.removeEventListener("click", onRemoveClick);
      btn.addEventListener("click", onRemoveClick);
    });
  }

  function onDrawerClose() {
    closeDrawer();
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDrawer();
  });

  /* ── wire full cart page (/cart) ────────────────────────── */

  function wireCartPage() {
    document.querySelectorAll("#main-cart-items .qty-btn").forEach((btn) => {
      btn.removeEventListener("click", onQtyClick);
      btn.addEventListener("click", onQtyClick);
    });
    document
      .querySelectorAll("#main-cart-items .qty-input")
      .forEach((input) => {
        input.removeEventListener("change", onQtyInputChange);
        input.addEventListener("change", onQtyInputChange);
      });
    document
      .querySelectorAll('#main-cart-items [data-action="remove"]')
      .forEach((btn) => {
        btn.removeEventListener("click", onRemoveClick);
        btn.addEventListener("click", onRemoveClick);
      });
    const clearBtn = document.getElementById("cart-clear-btn");
    if (clearBtn) {
      clearBtn.removeEventListener("click", onClearCart);
      clearBtn.addEventListener("click", onClearCart);
    }
  }

  /* ── qty / remove handlers ─────────────────────────────── */

  async function onQtyClick(e) {
    const btn = e.currentTarget;
    const action = btn.dataset.action;
    const key = btn.dataset.key;
    if (!key) return;
    const container =
      btn.closest(".cart-line") || btn.closest(".cd-line");
    const qtyInput = container && container.querySelector(".qty-input");
    const qtyVal = container && container.querySelector(".qty-val");
    const current = parseInt(
      (qtyInput || qtyVal)?.value || qtyVal?.textContent || "1",
      10
    );
    const next =
      action === "increase" ? current + 1 : Math.max(0, current - 1);
    await mutate(() =>
      cartFetch("/cart/change.js", { id: key, quantity: next })
    );
  }

  async function onQtyInputChange(e) {
    const input = e.currentTarget;
    const key = input.dataset.key;
    const qty = parseInt(input.value, 10);
    if (!key || isNaN(qty) || qty < 0) return;
    await mutate(() =>
      cartFetch("/cart/change.js", { id: key, quantity: qty })
    );
  }

  async function onRemoveClick(e) {
    const key = e.currentTarget.dataset.key;
    if (key)
      await mutate(() =>
        cartFetch("/cart/change.js", { id: key, quantity: 0 })
      );
  }

  async function onClearCart() {
    await mutate(() => cartFetch("/cart/clear.js", {}));
  }

  /* ── header cart link → open drawer ────────────────────── */

  function wireHeaderCart() {
    const cartLink = document.querySelector(".topnav .cart");
    if (cartLink)
      cartLink.addEventListener("click", (e) => {
        e.preventDefault();
        openDrawer(cartLink);
      });
  }

  /* ── product forms (add to cart) ────────────────────────── */

  function wireProductForms() {
    document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      if (form.dataset.wired) return;
      form.dataset.wired = "true";
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const id = formData.get("id");
        const qty = parseInt(formData.get("quantity") || "1", 10);
        const silent = form.querySelector("[data-silent]") !== null;
        await mutate(
          () => cartFetch("/cart/add.js", { id, quantity: qty }),
          silent ? "Added to cart" : null
        );
        if (!silent) openDrawer(form.querySelector('[type="submit"]'));
      });
    });
  }

  /* ── cross-sell loader ─────────────────────────────────── */

  async function loadCrossSell() {
    const band = document.querySelector(".cart-cross[data-url]");
    if (!band) return;
    const grid = document.getElementById("cross-sell-grid");
    if (!grid) return;
    try {
      const res = await fetch(band.dataset.url, {
        headers: { Accept: "text/html" },
      });
      if (!res.ok) return;
      const html = await res.text();
      const scratch = document.createElement("div");
      scratch.innerHTML = html;
      const cards = scratch.querySelectorAll(".card-product");
      if (!cards.length) return;
      cards.forEach((card) => grid.appendChild(card));
      grid
        .querySelectorAll("[data-add-to-cart]")
        .forEach((btn) => btn.setAttribute("data-silent", ""));
      wireProductForms();
    } catch {}
  }

  /* ── visibility-change sync ────────────────────────────── */

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isMutating) {
      cartFetch("/cart.js")
        .then((cart) => {
          const cached = parseInt(
            document.querySelector(".topnav .cart .bubble")?.textContent ||
              "0",
            10
          );
          if (cart.item_count !== cached) reRenderSections();
        })
        .catch(() => {});
    }
  });

  /* ── init ──────────────────────────────────────────────── */

  function init() {
    wireHeaderCart();
    wireCartPage();
    wireDrawer();
    wireProductForms();
    loadCrossSell();
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", init);
  else init();
})();
